import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import type { Configuracao, CreateConfiguracaoRequest } from '../types';
import { configuracoesApi } from '../services/api';
import { Plus, Edit, Save, X, Trash2, Settings2 } from 'lucide-react';

interface ConfigForm {
  chave: string;
  valor: string;
  descricao: string;
}

const emptyForm: ConfigForm = { chave: '', valor: '', descricao: '' };

export function ConfiguracoesPage() {
  const { isAdmin } = useAuth();
  const { success, error } = useToast();
  const [configs, setConfigs] = useState<Configuracao[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<ConfigForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const loadConfigs = useCallback(async () => {
    try {
      const response = await configuracoesApi.getAll();
      if (response.success && response.data) {
        setConfigs(response.data);
      }
    } catch {
      error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await configuracoesApi.getAll();
        if (active && response.success && response.data) {
          setConfigs(response.data);
        }
      } catch {
        if (active) error('Erro ao carregar configurações');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [error]);

  const startEdit = (config: Configuracao) => {
    setEditingId(config.id);
    setEditValue(config.valor);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (config: Configuracao) => {
    try {
      const response = await configuracoesApi.update(config.id, { valor: editValue, descricao: config.descricao, versao: config.versao || 1 });
      if (response.success) {
        success('Configuração atualizada');
        loadConfigs();
        cancelEdit();
      } else {
        error(response.message || 'Erro ao atualizar configuração');
      }
    } catch {
      error('Erro ao atualizar configuração');
    }
  };

  const openNovo = () => {
    setForm(emptyForm);
    setFormError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.chave.trim()) {
      setFormError('A chave é obrigatória');
      return;
    }
    if (!form.valor.trim()) {
      setFormError('O valor é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const request: CreateConfiguracaoRequest = {
        chave: form.chave.trim(),
        valor: form.valor.trim(),
        descricao: form.descricao.trim() || undefined,
      };
      const response = await configuracoesApi.create(request);
      if (response.success) {
        success('Configuração criada com sucesso');
        setModalOpen(false);
        loadConfigs();
      } else {
        setFormError(response.message || 'Erro ao criar configuração');
      }
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Erro ao criar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (config: Configuracao) => {
    if (!confirm(`Eliminar a configuração "${config.chave}"?`)) return;
    try {
      const response = await configuracoesApi.delete(config.id);
      if (response.success) {
        success('Configuração eliminada');
        loadConfigs();
      } else {
        error(response.message || 'Erro ao eliminar configuração');
      }
    } catch {
      error('Erro ao eliminar configuração');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Configurações</h1>
        <div className="header-actions">
          {isAdmin && (
            <button className="btn btn-primary" onClick={openNovo}>
              <Plus className="w-4 h-4" />
              Nova Configuração
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="page-loading">
            <div className="spinner"></div>
            <p>A carregar configurações...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Chave</th>
                  <th>Valor</th>
                  <th>Descrição</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {configs.map(config => (
                  <tr key={config.id}>
                    <td className="font-mono text-sm">{config.chave}</td>
                    <td>
                      {editingId === config.id ? (
                        <div className="edit-form">
                          <input
                            type="text"
                            className="input input-sm"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            autoFocus
                          />
                          <div className="edit-actions">
                            <button className="btn btn-primary btn-sm" onClick={() => saveEdit(config)}>
                              <Save className="w-4 h-4" />
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="font-mono text-sm">{config.valor}</span>
                      )}
                    </td>
                    <td>{config.descricao || '—'}</td>
                    <td>
                      {isAdmin ? (
                        <div className="table-actions">
                          {editingId === config.id ? (
                            <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>
                              <X className="w-4 h-4" />
                            </button>
                          ) : (
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => startEdit(config)} aria-label="Editar">
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={() => handleEliminar(config)} aria-label="Eliminar">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && configs.length === 0 && (
          <div className="empty-state">
            <Settings2 className="w-12 h-12 text-gray-300" />
            <h3>Nenhuma configuração</h3>
            <p>Adicione a primeira configuração</p>
            {isAdmin && (
              <button className="btn btn-primary mt-4" onClick={openNovo}>
                <Plus className="w-4 h-4" />
                Nova Configuração
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        title="Nova Configuração"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" form="config-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'A guardar...' : 'Criar'}
            </button>
          </>
        }
      >
        <form id="config-form" onSubmit={handleSubmit} noValidate>
          {formError && (
            <div className="form-error" role="alert">
              {formError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="chave" className="label">Chave <span className="required">*</span></label>
            <input
              type="text"
              id="chave"
              className="input font-mono"
              value={form.chave}
              onChange={e => setForm(p => ({ ...p, chave: e.target.value }))}
              placeholder="Ex: MAX_RESERVAS_POR_DIA"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="valor" className="label">Valor <span className="required">*</span></label>
            <input
              type="text"
              id="valor"
              className="input font-mono"
              value={form.valor}
              onChange={e => setForm(p => ({ ...p, valor: e.target.value }))}
              placeholder="Valor da configuração"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="descricao" className="label">Descrição</label>
            <input
              type="text"
              id="descricao"
              className="input"
              value={form.descricao}
              onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))}
              placeholder="Breve descrição (opcional)"
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}